import { storage } from '@qse/utils'
import { fetchAuthSession } from 'aws-amplify/auth'
import { toast } from 'sonner'

export interface SessionData {
  userId: string
  email: string
  deviceId: string
  createdAt: number
  lastActivity: number
  expiresAt: number
  isValid: boolean
}

export interface SessionConfig {
  maxSessionLifetime: number // 7 days in milliseconds
  refreshInterval: number // 5 minutes in milliseconds
  activityTimeout: number // 30 minutes in milliseconds
}

export class SessionManager {
  private static instance: SessionManager
  private refreshTimer?: ReturnType<typeof setInterval>
  private activityTimer?: ReturnType<typeof setTimeout>
  private config: SessionConfig = {
    maxSessionLifetime: 7 * 24 * 60 * 60 * 1000, // 7 days
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    activityTimeout: 30 * 60 * 1000, // 30 minutes
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager()
    }

    return SessionManager.instance
  }

  // Generate unique device ID
  private generateDeviceId(): string {
    const existingId = storage.localStorage.getItem('device_id')

    if (existingId) return existingId

    const deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    storage.localStorage.setItem('device_id', deviceId)

    return deviceId
  }

  // Create new session
  async createSession(userId: string, email: string): Promise<SessionData> {
    const deviceId = this.generateDeviceId()
    const now = Date.now()

    const sessionData: SessionData = {
      userId,
      email,
      deviceId,
      createdAt: now,
      lastActivity: now,
      expiresAt: now + this.config.maxSessionLifetime,
      isValid: true,
    }

    // Store session data
    storage.localStorage.setItem('session_data', JSON.stringify(sessionData))

    // Start session monitoring
    this.startSessionMonitoring()

    // Set up activity tracking
    this.setupActivityTracking()

    return sessionData
  }

  // Get current session
  getCurrentSession(): SessionData | null {
    try {
      const sessionData = storage.localStorage.getItem('session_data')

      if (!sessionData) return null

      const session: SessionData = JSON.parse(sessionData)

      // Check if session is expired
      if (Date.now() > session.expiresAt) {
        this.clearSession()

        return null
      }

      // Check if session is inactive
      if (Date.now() - session.lastActivity > this.config.activityTimeout) {
        this.clearSession()

        return null
      }

      return session
    } catch (error) {
      console.error('Error parsing session data:', error)

      return null
    }
  }

  // Update session activity
  updateActivity(): void {
    const session = this.getCurrentSession()

    if (!session) return

    session.lastActivity = Date.now()
    storage.localStorage.setItem('session_data', JSON.stringify(session))
  }

  // Refresh session
  async refreshSession(): Promise<boolean> {
    try {
      const session = this.getCurrentSession()

      if (!session) return false

      // Check if we can get a valid auth session from AWS
      const authSession = await fetchAuthSession()

      if (!authSession.tokens?.accessToken) {
        this.clearSession()

        return false
      }

      // Update session activity
      session.lastActivity = Date.now()
      storage.localStorage.setItem('session_data', JSON.stringify(session))

      return true
    } catch (error) {
      console.error('Error refreshing session:', error)
      this.clearSession()

      return false
    }
  }

  // Clear session
  clearSession(): void {
    storage.localStorage.removeItem('session_data')
    storage.localStorage.removeItem('token')
    storage.localStorage.removeItem('temp_email')
    storage.localStorage.removeItem('temp_session')
    storage.localStorage.removeItem('totp_setup_data')
    storage.localStorage.removeItem('user_first_login')
    storage.localStorage.removeItem('totp_setup_skipped')
    storage.localStorage.removeItem('user_first_login_complete')
    storage.localStorage.removeItem('isMfaVerified')
    storage.localStorage.removeItem('mfaStepCompleted')

    this.stopSessionMonitoring()
  }

  // Start session monitoring
  private startSessionMonitoring(): void {
    this.stopSessionMonitoring()

    this.refreshTimer = setInterval(async () => {
      const session = this.getCurrentSession()

      if (!session) {
        this.stopSessionMonitoring()

        return
      }

      const refreshed = await this.refreshSession()

      if (!refreshed) {
        toast.error('Session expired. Please log in again.')
        this.stopSessionMonitoring()
      }
    }, this.config.refreshInterval)
  }

  // Stop session monitoring
  private stopSessionMonitoring(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = undefined
    }
    if (this.activityTimer) {
      clearTimeout(this.activityTimer)
      this.activityTimer = undefined
    }
  }

  // Setup activity tracking
  private setupActivityTracking(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    const updateActivity = () => {
      this.updateActivity()
    }

    events.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })

    // Store event listeners for cleanup
    storage.localStorage.setItem('activity_listeners', JSON.stringify(events))
  }

  // Cleanup activity tracking
  private cleanupActivityTracking(): void {
    try {
      const events = JSON.parse(storage.localStorage.getItem('activity_listeners') || '[]')
      const updateActivity = () => this.updateActivity()

      events.forEach((event: string) => {
        document.removeEventListener(event, updateActivity, true)
      })
    } catch (error) {
      console.error('Error cleaning up activity tracking:', error)
    }
  }

  // Check if session is valid
  isSessionValid(): boolean {
    const session = this.getCurrentSession()

    return session !== null
  }

  // Get session info
  getSessionInfo(): { deviceId: string; expiresAt: number; lastActivity: number } | null {
    const session = this.getCurrentSession()

    if (!session) return null

    return {
      deviceId: session.deviceId,
      expiresAt: session.expiresAt,
      lastActivity: session.lastActivity,
    }
  }

  // Handle browser close
  handleBrowserClose(): void {
    // Clear all session data when browser closes
    this.clearSession()
    this.cleanupActivityTracking()
  }

  // Setup beforeunload listener
  setupBeforeUnloadListener(): void {
    const handleBeforeUnload = () => {
      this.handleBrowserClose()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Store reference for cleanup
    storage.localStorage.setItem('beforeunload_listener', 'true')
  }

  // Cleanup beforeunload listener
  cleanupBeforeUnloadListener(): void {
    const handleBeforeUnload = () => {
      this.handleBrowserClose()
    }

    window.removeEventListener('beforeunload', handleBeforeUnload)
    storage.localStorage.removeItem('beforeunload_listener')
  }

  // Initialize session manager
  initialize(): void {
    // Setup beforeunload listener for browser close handling
    this.setupBeforeUnloadListener()

    // Check for existing session
    const session = this.getCurrentSession()

    if (session) {
      this.startSessionMonitoring()
      this.setupActivityTracking()
    }
  }

  // Cleanup session manager
  cleanup(): void {
    this.stopSessionMonitoring()
    this.cleanupActivityTracking()
    this.cleanupBeforeUnloadListener()
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance()
